import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { IPremadeQuiz, RoutePath } from '@quizzach/interfaces';
import { Reveal, useMarqueeDrag } from '@quizzach/motion';
import { CollabStripBox, Marquee } from '@quizzach/styles';
import { scrollToTop } from '@quizzach/utils';

import { CardAuthorMark } from './AuthorMark';

/** safety net for the widen-until-it-fills loop below */
const MAX_REPS = 8;

interface Collaborator {
	name: string;
	slug: string;
}

/**
 * Guest authors in the order the collection introduces them — quizzach signs
 * its own quizzes, so it isn't a collaborator the strip advertises.
 */
const collaboratorsFrom = (quizzes: IPremadeQuiz[]): Collaborator[] => {
	const bySlug = new Map<string, Collaborator>();

	quizzes.forEach((quiz) => {
		const name = quiz.author?.display_name;
		const slug = quiz.author?.display_name_slug;
		if (!name || !slug || name === 'quizzach' || bySlug.has(slug)) return;
		bySlug.set(slug, { name, slug });
	});

	return [...bySlug.values()];
};

/**
 * Collaborators as an endless roll under a handwritten note: the guest logos
 * ride the same draggable marquee the homepage premade rows use, so the
 * strip auto-scrolls, pauses on hover and can be swiped left/right on touch.
 * It creeps along far slower than the homepage rows: it is a signature the
 * eye catches in passing, not a second row of tickets competing for it.
 *
 * The track carries its content twice and wraps at half its scroll width, so
 * both halves must be identical; `reps` grows until one half is wider than
 * the viewport, otherwise the wrap would expose a gap on wide screens.
 */
export const CollabStrip = ({ quizzes }: { quizzes: IPremadeQuiz[] }) => {
	const { t } = useTranslation();
	const boxRef = useRef<HTMLDivElement>(null);
	const trackRef = useRef<HTMLDivElement>(null);
	const [reps, setReps] = useState(1);

	const collaborators = collaboratorsFrom(quizzes);

	useMarqueeDrag(trackRef, false, collaborators.length * reps, 60);

	// re-measured through a ResizeObserver rather than on render: the logos
	// only get their width once the images load, well after the last render
	useLayoutEffect(() => {
		const box = boxRef.current;
		const track = trackRef.current;
		if (!box || !track) return;

		const measure = () => {
			const half = track.scrollWidth / 2;
			if (half > 0 && half < box.clientWidth) {
				setReps((r) => (r < MAX_REPS ? r + 1 : r));
			}
		};

		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(track);
		observer.observe(box);
		return () => observer.disconnect();
	}, [collaborators.length]);

	if (collaborators.length === 0) return null;

	const half = Array.from({ length: reps }).flatMap(() => collaborators);

	return (
		<Reveal>
			<CollabStripBox ref={boxRef}>
				<span className='lead'>{t('premade.collab.hand')}</span>
				<span className='rail'>
					<Marquee ref={trackRef} className='track'>
						{[0, 1].map((copy) =>
							half.map((collaborator, i) => (
								<Fragment key={`${copy}-${i}-${collaborator.slug}`}>
									<Link
										className='mark'
										to={`${RoutePath.PremadeQuizzes}/author/${collaborator.slug}`}
										onClick={scrollToTop}
									>
										<CardAuthorMark displayName={collaborator.name} />
									</Link>
									<span className='dot' aria-hidden='true' />
								</Fragment>
							))
						)}
					</Marquee>
				</span>
			</CollabStripBox>
		</Reveal>
	);
};
